<script setup>
import { computed } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import { PhArrowLeft, PhReceipt, PhUser, PhBank, PhDeviceMobile } from '@phosphor-icons/vue';
import Layout from '@pages/Shared/layouts/AdminLayout.vue';

const props = defineProps({
    payment: Object,
});

const p = computed(() => props.payment);

// ── Helpers ────────────────────────────────────────────────────────────────

// Safely resolve a PHP enum cast — Inertia sends { name, value } objects
const resolveEnum = (val) => {
    if (!val) return null;
    if (typeof val === 'string') return val;
    if (typeof val === 'object' && val.value) return val.value;
    return String(val);
};

const formatEnum = (val) =>
    (resolveEnum(val) ?? '').replace(/_/g, ' ');

const naira = (val) =>
    '₦' + Number(val ?? 0).toLocaleString('en-NG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const formatDate = (val, opts = {}) =>
    val ? new Date(val).toLocaleString('en-NG', { day: '2-digit', month: 'short', year: 'numeric', ...opts }) : '—';

// ── Derived values ─────────────────────────────────────────────────────────

const student = computed(() => p.value.student_fee?.student ?? null);
const fee     = computed(() => p.value.student_fee?.fee     ?? null);

const studentName = computed(() => {
    if (!student.value) return '—';
    return `${student.value.first_name ?? ''} ${student.value.last_name ?? ''}`.trim() || '—';
});

const receivedByName = computed(() => {
    const r = p.value.received_by;
    if (!r) return '—';
    return `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || r.name || '—';
});

const verifiedByName = computed(() => {
    const v = p.value.verified_by;
    if (!v) return '—';
    return `${v.first_name ?? ''} ${v.last_name ?? ''}`.trim() || v.name || '—';
});

const isPartial = computed(() =>
    Number(p.value.amount) !== Number(p.value.amount_paid)
);

const isOnline = computed(() =>
    resolveEnum(p.value.payment_method) === 'paystack'
);

const initiatedBy = computed(() => resolveEnum(p.value.initiated_by));

// ── Status / initiator colours ─────────────────────────────────────────────

const statusColor = (val) => ({
    paid:     'bg-green-50 text-green-700 ring-green-200',
    pending:  'bg-yellow-50 text-yellow-700 ring-yellow-200',
    unpaid:   'bg-red-50 text-red-600 ring-red-200',
    failed:   'bg-red-50 text-red-600 ring-red-200',
    reversed: 'bg-orange-50 text-orange-600 ring-orange-200',
    waived:   'bg-gray-100 text-gray-500 ring-gray-200',
    partial:  'bg-primary-50 text-primary-700 ring-primary-200',
}[resolveEnum(val)] ?? 'bg-gray-100 text-gray-500 ring-gray-200');

const initiatorColor = (val) => ({
    student: 'bg-purple-50 text-purple-700',
    cashier: 'bg-amber-50 text-amber-700',
    system:  'bg-gray-100 text-gray-500',
}[val] ?? 'bg-gray-100 text-gray-500');
</script>

<template>
    <Layout>
        <Head :title="`Payment — ${p.reference}`" />

        <!-- ── Back + Title ─────────────────────────────────────────────── -->
        <div class="flex items-center gap-3 mb-6">
            <button
                @click="router.get('/admin/payments')"
                class="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition"
            >
                <PhArrowLeft :size="20" />
            </button>
            <div>
                <h2 class="text-xl font-bold text-primary">Payment Detail</h2>
                <p class="text-sm text-gray-500 font-mono mt-0.5">{{ p.reference }}</p>
            </div>
        </div>

        <div class="max-w-2xl space-y-4">

            <!-- ── Receipt card ─────────────────────────────────────────── -->
            <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

                <!-- Header row: receipt icon + status + initiator badges -->
                <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
                    <div class="flex items-center gap-2">
                        <PhReceipt :size="20" class="text-primary" />
                        <span class="font-semibold text-gray-800">Receipt</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <!-- Who initiated -->
                        <span
                            class="inline-flex items-center px-2.5 py-0.5 rounded-full
                                   text-xs font-medium capitalize"
                            :class="initiatorColor(initiatedBy)"
                        >
                            {{ formatEnum(p.initiated_by) || '—' }}
                        </span>
                        <!-- Payment status -->
                        <span
                            class="inline-flex items-center px-3 py-1 rounded-full
                                   text-sm font-medium capitalize ring-1"
                            :class="statusColor(p.status)"
                        >
                            {{ resolveEnum(p.status) || '—' }}
                        </span>
                    </div>
                </div>

                <!-- ── Amount highlight ────────────────────────────────── -->
                <div class="px-6 py-5 border-b border-gray-100 flex items-end justify-between gap-4">
                    <div>
                        <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">Amount Paid</p>
                        <p class="text-3xl font-bold text-primary">{{ naira(p.amount_paid) }}</p>
                        <p v-if="isPartial" class="text-sm text-gray-400 line-through mt-0.5">
                            Expected {{ naira(p.amount) }}
                        </p>
                    </div>
                    <div class="text-right">
                        <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">Method</p>
                        <div class="flex items-center gap-1.5 justify-end">
                            <component
                                :is="isOnline ? PhDeviceMobile : PhBank"
                                :size="16"
                                class="text-gray-400"
                            />
                            <span class="text-sm font-medium text-gray-700 capitalize">
                                {{ formatEnum(p.payment_method) || '—' }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- ── Details grid ────────────────────────────────────── -->
                <div class="divide-y divide-gray-100">
                    <div
                        v-for="row in [
                            { label: 'Reference',   value: p.reference },
                            { label: 'Session',     value: p.academic_session?.name ?? '—' },
                            { label: 'Term',        value: p.academic_term?.name   ?? '—' },
                            { label: 'Date Paid',   value: formatDate(p.paid_at, { hour: '2-digit', minute: '2-digit' }) },
                            { label: 'Received by', value: receivedByName },
                            { label: 'Verified by', value: verifiedByName },
                            { label: 'Verified at', value: formatDate(p.verified_at, { hour: '2-digit', minute: '2-digit' }) },
                        ]"
                        :key="row.label"
                        class="grid grid-cols-2 px-6 py-3 text-sm"
                    >
                        <span class="text-gray-400">{{ row.label }}</span>
                        <span class="text-gray-800 font-medium">{{ row.value }}</span>
                    </div>

                    <!-- Description -->
                    <div v-if="p.description" class="px-6 py-3 text-sm">
                        <p class="text-gray-400 mb-1">Description</p>
                        <p class="text-gray-700">{{ p.description }}</p>
                    </div>

                    <!-- Notes -->
                    <div v-if="p.notes" class="px-6 py-3 text-sm">
                        <p class="text-gray-400 mb-1">Notes</p>
                        <p class="text-gray-700">{{ p.notes }}</p>
                    </div>
                </div>
            </div>

            <!-- ── Student card ─────────────────────────────────────────── -->
            <div
                v-if="student"
                class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
                <div class="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                    <PhUser :size="18" class="text-primary" />
                    <span class="font-semibold text-gray-800">Student</span>
                </div>
                <div class="divide-y divide-gray-100">
                    <div
                        v-for="row in [
                            { label: 'Name',             value: studentName },
                            { label: 'Admission No.',    value: student.admission_number ?? '—' },
                            { label: 'Class',            value: student.class_room?.name ?? '—' },
                        ]"
                        :key="row.label"
                        class="grid grid-cols-2 px-6 py-3 text-sm"
                    >
                        <span class="text-gray-400">{{ row.label }}</span>
                        <span class="text-gray-800 font-medium">{{ row.value }}</span>
                    </div>
                </div>
            </div>

            <!-- ── Fee card ─────────────────────────────────────────────── -->
            <div
                v-if="fee"
                class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
                <div class="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                    <PhReceipt :size="18" class="text-primary" />
                    <span class="font-semibold text-gray-800">Fee</span>
                </div>
                <div class="divide-y divide-gray-100">
                    <div
                        v-for="row in [
                            { label: 'Title',        value: fee.title },
                            { label: 'Category',     value: formatEnum(fee.category) },
                            { label: 'Fee Amount',   value: naira(p.student_fee?.amount) },
                            { label: 'Total Paid',   value: naira(p.student_fee?.amount_paid) },
                            { label: 'Balance Left', value: naira(p.student_fee?.balance) },
                            { label: 'Fee Status',   value: resolveEnum(p.student_fee?.status) },
                        ]"
                        :key="row.label"
                        class="grid grid-cols-2 px-6 py-3 text-sm"
                    >
                        <span class="text-gray-400">{{ row.label }}</span>
                        <span class="text-gray-800 font-medium capitalize">{{ row.value }}</span>
                    </div>

                    <!-- Discount row — only shown if a discount was applied -->
                    <div
                        v-if="Number(p.student_fee?.discount_amount) > 0"
                        class="grid grid-cols-2 px-6 py-3 text-sm"
                    >
                        <span class="text-gray-400">Discount Applied</span>
                        <div>
                            <p class="text-amber-600 font-medium">
                                {{ naira(p.student_fee.discount_amount) }}
                            </p>
                            <p v-if="p.student_fee.discount_reason" class="text-xs text-gray-400 mt-0.5">
                                {{ p.student_fee.discount_reason }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── Paystack details (online payments only) ──────────────── -->
            <div
                v-if="isOnline && p.paystack_reference"
                class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
                <div class="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                    <PhDeviceMobile :size="18" class="text-primary" />
                    <span class="font-semibold text-gray-800">Paystack</span>
                </div>
                <div class="divide-y divide-gray-100">
                    <div
                        v-for="row in [
                            { label: 'Paystack Reference', value: p.paystack_reference },
                            { label: 'Paystack Status',    value: p.paystack_status    ?? '—' },
                            { label: 'Channel',            value: p.paystack_channel   ?? '—' },
                        ]"
                        :key="row.label"
                        class="grid grid-cols-2 px-6 py-3 text-sm"
                    >
                        <span class="text-gray-400">{{ row.label }}</span>
                        <span class="text-gray-800 font-mono text-xs">{{ row.value }}</span>
                    </div>
                </div>
            </div>

        </div>
    </Layout>
</template>